import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { Users, MagnifyingGlass, CaretLeft, CaretRight, FileXls, FileCsv, CaretDown, CaretUp, Trash, Plus, FloppyDisk, Warning } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';
import { formatDate } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';

// Inline Auto-Save Label Input — debounced 500ms
function MerkerLabelInput({ id, initialLabel, saving, onSave }: {
  id: number; initialLabel: string; saving: boolean; onSave: (id: number, label: string) => void;
}) {
  const [value, setValue] = useState(initialLabel);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sync wenn extern reload (z.B. nach loadMerkerData)
  useEffect(() => { setValue(initialLabel); }, [initialLabel]);
  const handleChange = (v: string) => {
    setValue(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { onSave(id, v); }, 500);
  };
  const handleBlur = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (value !== initialLabel) onSave(id, value);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <input
        type="text"
        value={value}
        placeholder="Label eingeben..."
        onChange={e => handleChange(e.target.value)}
        onBlur={handleBlur}
        style={{
          flex: 1, padding: '0.3rem 0.5rem',
          border: '1px solid #d1d5db', borderRadius: '4px',
          fontSize: '0.85rem', backgroundColor: 'white',
        }}
      />
      {saving && <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>speichert…</span>}
    </div>
  );
}

interface MubRecord {
  id: number;
  ident_nr: number;
  kunden_nr: number;
  mandant: string;
  anrede_nr: number;
  titel: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  strasse: string;
  haus_nr: string;
  plz: string;
  ort: string;
  land: string;
  rufnummer1: string;
  email1: string;
  opt_in_telefon: number;
  opt_in_email: number;
  opt_in_post: number;
  individual_merker: string[] | null;
  last_export_date: string;
  kunden_art: number;
  klalo_wkz: number;
  beginn_lotterie: number;
  beginn_klasse: number;
  absprung_lotterie: number;
  absprung_klasse: number;
  lose_preis_gesamt: number;
}

interface Statistics {
  total: number;
  byMandant: { mandant: string; count: number }[];
  byKundenArt: { kunden_art: number; count: number }[];
  optIn: { telefon_ja: number; telefon_nein: number; telefon_ohne: number; email_ja: number; email_nein: number; email_ohne: number };
}

interface MerkerCode {
  id: number;
  code: string;
  label: string;
  created_at: string;
  updated_at: string;
  usage_count?: number;
  is_unlabeled?: number;
}

const MANDANT_LABELS: Record<string, string> = {
  BOH: 'NKL (BOH)',
  BOK: 'SKL (BOK)',
  RLT: 'Renten (RLT)',
};

const MANDANT_COLORS: Record<string, string> = {
  BOH: '#2563eb',
  BOK: '#059669',
  RLT: '#7c3aed',
};

// MuB-OptIn-Codierung (xpecto): 0 = ohne/unbekannt, 1 = Nein, 2 = Ja
function renderOptInPill(value: number) {
  const v = Number(value);
  const cfg = v === 2
    ? { bg: '#dcfce7', fg: '#166534', lbl: 'Ja' }
    : v === 1
    ? { bg: '#fecaca', fg: '#991b1b', lbl: 'Nein' }
    : { bg: '#f3f4f6', fg: '#6b7280', lbl: 'Ohne' };
  return (
    <span style={{ padding: '1px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, backgroundColor: cfg.bg, color: cfg.fg }}>
      {cfg.lbl}
    </span>
  );
}

const KUNDEN_ART_LABELS: Record<number, string> = {
  0: 'Nicht angelegt',
  1: 'Aktiv',
  2: 'Absprung',
  3: 'Gekündigt',
  4: 'Passiv',
  5: 'Gesperrt',
  6: 'Gestorben',
  9: 'Pass.wg Absprg',
};

export default function MubView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [records, setRecords] = useState<MubRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [filterMandant, setFilterMandant] = useState('');
  const [filterKundenArt, setFilterKundenArt] = useState('');
  const [filterOptIn, setFilterOptIn] = useState('');

  const [stats, setStats] = useState<Statistics | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Export states
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportingS001, setExportingS001] = useState(false);

  // IndividualMerker states
  const [merkerOpen, setMerkerOpen] = useState(false);
  const [merkerCodes, setMerkerCodes] = useState<MerkerCode[]>([]);
  const [usedCodes, setUsedCodes] = useState<string[]>([]);
  const [merkerLoading, setMerkerLoading] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const totalPages = Math.ceil(total / limit);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterMandant) params.set('mandant', filterMandant);
    if (filterKundenArt) params.set('kunden_art', filterKundenArt);
    if (filterOptIn) params.set('opt_in_telefon', filterOptIn);
    return params;
  }, [search, filterMandant, filterKundenArt, filterOptIn]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      const res = await authFetch(getApiUrl(`/mub?${params.toString()}`));
      const data = await res.json();
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, limit, buildFilterParams]);

  const loadStatistics = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await authFetch(getApiUrl('/mub/statistics'));
      const data = await res.json();
      setStats(data);
    } catch { /* ignore */ }
    setLoadingStats(false);
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadStatistics(); }, [loadStatistics]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  // ============= Export Handlers =============
  const handleExportXlsx = async () => {
    setExportingXlsx(true);
    try {
      const params = buildFilterParams();
      const res = await authFetch(getApiUrl(`/mub/export?${params.toString()}`));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MuB_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed:', e); }
    setExportingXlsx(false);
  };

  const handleExportS001 = async () => {
    setExportingS001(true);
    try {
      const res = await authFetch(getApiUrl('/mub/export-s001'));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `S001_Dialfire_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('S001 Export failed:', e); }
    setExportingS001(false);
  };

  // ============= IndividualMerker Handlers =============
  const loadMerkerData = async () => {
    setMerkerLoading(true);
    try {
      const [codesRes, usedRes] = await Promise.all([
        authFetch(getApiUrl('/mub/merker-codes')),
        authFetch(getApiUrl('/mub/merker-codes/used')),
      ]);
      setMerkerCodes(await codesRes.json());
      setUsedCodes(await usedRes.json());
    } catch { /* ignore */ }
    setMerkerLoading(false);
  };

  const handleMerkerToggle = () => {
    const next = !merkerOpen;
    setMerkerOpen(next);
    if (next) loadMerkerData();
  };

  const handleAutoSaveLabel = async (id: number, label: string) => {
    setSavingIds(prev => { const n = new Set(prev); n.add(id); return n; });
    try {
      await authFetch(getApiUrl(`/mub/merker-codes/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      setMerkerCodes(prev => prev.map(c => c.id === id ? { ...c, label } : c));
    } catch { /* ignore */ }
    finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const handleDeleteMerker = async (id: number, code: string) => {
    if (!window.confirm(`Code "${code}" wirklich löschen?`)) return;
    try {
      await authFetch(getApiUrl(`/mub/merker-codes/${id}`), { method: 'DELETE' });
      loadMerkerData();
    } catch { /* ignore */ }
  };

  const handleAddMerker = async () => {
    if (!newCode.trim()) return;
    try {
      await authFetch(getApiUrl('/mub/merker-codes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newCode.trim(), label: newLabel.trim() }),
      });
      setNewCode('');
      setNewLabel('');
      loadMerkerData();
    } catch { /* ignore */ }
  };

  const handleSaveUnmapped = async (code: string, label: string) => {
    try {
      await authFetch(getApiUrl('/mub/merker-codes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, label }),
      });
      loadMerkerData();
    } catch { /* ignore */ }
  };

  // Unmapped codes: in Daten vorhanden aber kein Label definiert
  const mappedCodes = new Set(merkerCodes.map(m => m.code));
  const unmappedCodes = usedCodes.filter(c => !mappedCodes.has(c));

  const btnStyle = (color: string, disabled: boolean) => ({
    padding: '0.5rem 1rem',
    backgroundColor: disabled ? '#9ca3af' : color,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600 as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '0.4rem',
    whiteSpace: 'nowrap' as const,
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <SubPageHeader title="MUB Daten (Spielteilnehmer)" />

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>
        <h1 style={{ color: '#003366', margin: '0 0 1.5rem 0', fontSize: isMobile ? '1.5rem' : '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Users size={28} weight="fill" /> MUB Daten (Spielteilnehmer)
        </h1>

        {/* Statistik-Karten */}
        {stats && !loadingStats && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Gesamt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#003366' }}>{stats.total.toLocaleString()}</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Mandant</div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                {stats.byMandant.map(m => (
                  <div key={m.mandant}>
                    <span style={{ fontWeight: 700, fontSize: '1.1rem', color: MANDANT_COLORS[m.mandant] || '#6b7280' }}>{Number(m.count).toLocaleString()}</span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.25rem' }}>{m.mandant}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filter-Leiste */}
        <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Suche (Name/PLZ/Ort/KundenNr)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Name, PLZ, Ort, Kundennummer..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
                />
                <button onClick={handleSearch} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                  <MagnifyingGlass size={16} weight="bold" />
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Mandant</label>
              <select value={filterMandant} onChange={e => { setFilterMandant(e.target.value); setPage(1); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}>
                <option value="">Alle</option>
                <option value="BOH">BOH - NKL</option>
                <option value="BOK">BOK - SKL</option>
                <option value="RLT">RLT - Renten</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Kundenart</label>
              <select value={filterKundenArt} onChange={e => { setFilterKundenArt(e.target.value); setPage(1); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}>
                <option value="">Alle</option>
                <option value="1">Aktiv</option>
                <option value="2">Absprung</option>
                <option value="3">Gek&uuml;ndigt</option>
                <option value="4">Storno</option>
                <option value="9">Absprung (alt)</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>OptIn Telefon</label>
              <select value={filterOptIn} onChange={e => { setFilterOptIn(e.target.value); setPage(1); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}>
                <option value="">Alle</option>
                <option value="2">Ja</option>
                <option value="1">Nein</option>
                <option value="0">Ohne</option>
              </select>
            </div>

            {/* Export Buttons */}
            <button onClick={handleExportXlsx} disabled={exportingXlsx} style={btnStyle('#059669', exportingXlsx)}>
              <FileXls size={18} weight="bold" />
              {exportingXlsx ? 'Export...' : 'Excel Export'}
            </button>
          </div>
        </div>

        {/* Referenzdaten für Dialfire (S001) */}
        <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#003366', fontSize: '1.1rem' }}>Referenzdaten für Dialfire (S001)</h3>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#6b7280' }}>
            CSV-Export aus optin_cache mit MuB-Bestandsdaten (INF1–INF4) für Dialfire-Rückspielung.
          </p>
          <button onClick={handleExportS001} disabled={exportingS001} style={btnStyle('#d97706', exportingS001)}>
            <FileCsv size={18} weight="bold" />
            {exportingS001 ? 'Export...' : 'S001 Dialfire Export'}
          </button>
        </div>

        {/* IndividualMerker Verwaltung */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
          <button
            onClick={handleMerkerToggle}
            style={{
              width: '100%', padding: '1rem 1.5rem', backgroundColor: 'transparent', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontSize: '0.95rem', fontWeight: 600, color: '#003366',
            }}
          >
            {merkerOpen ? <CaretUp size={18} /> : <CaretDown size={18} />}
            IndividualMerker Verwaltung
            {unmappedCodes.length > 0 && (
              <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, marginLeft: '0.5rem' }}>
                {unmappedCodes.length} ohne Label
              </span>
            )}
          </button>

          {merkerOpen && (
            <div style={{ padding: '0 1.5rem 1.5rem' }}>
              {merkerLoading ? (
                <div style={{ color: '#6b7280', fontStyle: 'italic', padding: '1rem 0' }}>Lade...</div>
              ) : (
                <>
                  {/* Unmapped Codes */}
                  {unmappedCodes.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <Warning size={14} /> Codes ohne Label (in Daten vorhanden)
                      </div>
                      {unmappedCodes.map(code => (
                        <UnmappedRow key={code} code={code} onSave={handleSaveUnmapped} />
                      ))}
                    </div>
                  )}

                  {/* Bestehende Codes */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'left', width: '100px' }}>Code</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>Label</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right', width: '110px' }}>Verwendung</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center', width: '60px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {merkerCodes.map(m => (
                        <tr key={m.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontWeight: 600 }}>{m.code}</td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>
                            <MerkerLabelInput
                              id={m.id}
                              initialLabel={m.label || ''}
                              saving={savingIds.has(m.id)}
                              onSave={handleAutoSaveLabel}
                            />
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' }}>
                            {(m.usage_count || 0).toLocaleString('de-DE')}
                          </td>
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                            <button
                              onClick={() => handleDeleteMerker(m.id, m.code)}
                              style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                              title="Code löschen"
                            >
                              <Trash size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {merkerCodes.length === 0 && (
                        <tr><td colSpan={4} style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>Keine Codes definiert</td></tr>
                      )}
                    </tbody>
                  </table>

                  {/* Neuen Code hinzufügen */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
                    <Plus size={16} style={{ color: '#6b7280' }} />
                    <input
                      type="text"
                      placeholder="Code"
                      value={newCode}
                      onChange={e => setNewCode(e.target.value)}
                      style={{ width: '100px', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', fontFamily: 'monospace' }}
                    />
                    <input
                      type="text"
                      placeholder="Label"
                      value={newLabel}
                      onChange={e => setNewLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddMerker(); }}
                      style={{ flex: 1, padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                    />
                    <button
                      onClick={handleAddMerker}
                      disabled={!newCode.trim()}
                      style={{ padding: '0.4rem 0.75rem', backgroundColor: newCode.trim() ? '#003366' : '#9ca3af', color: 'white', border: 'none', borderRadius: '4px', cursor: newCode.trim() ? 'pointer' : 'not-allowed', fontSize: '0.8rem', fontWeight: 600 }}
                    >
                      Anlegen
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Datentabelle */}
        <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb' }}>
          {/* Header mit Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              {loading ? 'Lade...' : `${total.toLocaleString()} Ergebnisse`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>
                <CaretLeft size={16} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {page} / {totalPages || 1}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>
                <CaretRight size={16} />
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Mandant</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>KdNr</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Stra&szlig;e</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>PLZ/Ort</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Geb.</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Tel.OptIn</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Mail.OptIn</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Telefon</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>E-Mail</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>KdArt</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Lose &euro;</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '1px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: r.mandant === 'BOH' ? '#dbeafe' : r.mandant === 'BOK' ? '#dcfce7' : '#ede9fe',
                        color: MANDANT_COLORS[r.mandant] || '#6b7280',
                      }}>
                        {r.mandant}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.kunden_nr}</td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                      {r.titel ? <span style={{ color: '#6b7280', marginRight: '0.25rem' }}>{r.titel}</span> : null}
                      {r.vorname} {r.nachname}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.strasse}{r.haus_nr ? ` ${r.haus_nr}` : ''}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{r.plz} {r.ort}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {formatDate(r.geburtsdatum)}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      {renderOptInPill(r.opt_in_telefon)}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      {renderOptInPill(r.opt_in_email)}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.rufnummer1 || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email1 || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        color: r.kunden_art === 1 ? '#059669' : r.kunden_art === 2 ? '#d97706' : r.kunden_art === 9 ? '#dc2626' : '#6b7280',
                        fontWeight: r.kunden_art === 1 || r.kunden_art === 9 ? 600 : 400,
                      }}>
                        {KUNDEN_ART_LABELS[r.kunden_art] || (r.kunden_art != null ? `Art ${r.kunden_art}` : '-')}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {r.lose_preis_gesamt ? `${Number(r.lose_preis_gesamt).toFixed(2)}` : '-'}
                    </td>
                  </tr>
                ))}
                {records.length === 0 && !loading && (
                  <tr><td colSpan={12} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>Keine Daten gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination unten */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>
                <CaretLeft size={16} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>
                <CaretRight size={16} />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Sub-Komponente für Unmapped Codes
function UnmappedRow({ code, onSave }: { code: string; onSave: (code: string, label: string) => void }) {
  const [label, setLabel] = useState('');
  return (
    <div style={{
      display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0.5rem',
      backgroundColor: '#fef3c7', borderRadius: '4px', marginBottom: '0.3rem',
    }}>
      <span style={{ fontFamily: 'monospace', fontWeight: 600, width: '80px', color: '#92400e' }}>{code}</span>
      <input
        type="text"
        placeholder="Label eingeben..."
        value={label}
        onChange={e => setLabel(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && label.trim()) { onSave(code, label.trim()); setLabel(''); } }}
        style={{ flex: 1, padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
      />
      <button
        onClick={() => { if (label.trim()) { onSave(code, label.trim()); setLabel(''); } }}
        disabled={!label.trim()}
        style={{ padding: '0.25rem 0.5rem', backgroundColor: label.trim() ? '#059669' : '#9ca3af', color: 'white', border: 'none', borderRadius: '4px', cursor: label.trim() ? 'pointer' : 'not-allowed', fontSize: '0.8rem' }}
      >
        <FloppyDisk size={14} />
      </button>
    </div>
  );
}
